import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import { Form, Button, DropdownButton, Dropdown, Row, Col, Toast } from "react-bootstrap";
import { useFormik } from "formik";
import { IActionHandler, UserProfile, ActionHandlerFunction, AdminApprovalGroup, OrgInfo, AdminUserBase } from "@preveil-api";
import {
  AdminUIActionTypes, UserListActionTypes, useCreateOrgApprovalGroupMutation, Message, AdminSuccessMessages, AdminErrorMessages, MessageHandlerDisplayType, useAppDispatch, Account, AdminWarningMessages,
  SupportRoutes
} from "src/common";
import { Icon, UserList } from "src/components";
import { uiActions } from "src/store";
import _ from "lodash";
import * as Yup from "yup";

type AllProps = {
  approval_groups: AdminApprovalGroup[];
  copied_approval_group?: AdminApprovalGroup;
  org_full_users: AdminUserBase[];
  handleAction: ActionHandlerFunction;
  setCopiedApprovalGroup: Dispatch<SetStateAction<AdminApprovalGroup | undefined>>;
  current_account: Account;
  org_info: OrgInfo;
};

// Description: Form component for creating a new approval group
function CreateFormComponent(props: AllProps) {
  const { copied_approval_group, handleAction, setCopiedApprovalGroup, org_full_users, approval_groups, current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const [required_approvals, setRequiredApprovals] = useState<number>(2);
  const [editing_users, setEditingUsers] = useState<UserProfile[]>(!!copied_approval_group ? copied_approval_group.approvers : []);
  const [group_name, setGroupName] = useState<string | undefined>(!!copied_approval_group ? copied_approval_group.name : "");
  const user_list_ref = useRef<{ handlePageActions: ActionHandlerFunction }>();
  const group_name_ref = useRef<HTMLInputElement>(null);
  const [createApprovalGroup] = useCreateOrgApprovalGroupMutation();
  // Description: Formik hook for form validation and submission
  const formik = useFormik({
    validationSchema: Yup.object().shape({
      group_name: Yup
        .string()
        .required("The Approval Group Name is required")
        .test("group-name", AdminWarningMessages.group_name_exists, (value: string | undefined) => {
          if (value) {
            return !approval_groups.some((group) => group.name === value.trim());
          }
          return !!value;
        })
    }),
    initialValues: {
      group_name,
    },
    onSubmit: () => { }
  });

  // Description: useEffect hook to set the the editing users and group name to the copied approval group's info (if the user clicked copy in the toolbar)
  useEffect(() => {
    if (!!copied_approval_group) {
      setEditingUsers(copied_approval_group.approvers);
      setGroupName(copied_approval_group.name);
      setCopiedApprovalGroup(undefined);
    }

    if (group_name_ref && group_name_ref.current && !!group_name_ref.current.value) {
      // Note: if the group is copied from another group, we want to make sure to remind the user the group name is already taken.
      const { value } = group_name_ref.current;
      const current_group_name = value.trim();
      const is_group_name_found = approval_groups.some((group) => group.name === current_group_name);
      if (is_group_name_found) {
        formik.setErrors({ group_name: AdminWarningMessages.group_name_exists });
      }
    }
  }, []);

  // Description: Handles changes to the editing user list (add/remove users)
  function handleUpdateUsers(action_obj: IActionHandler) {
    const params = action_obj.params;
    setEditingUsers(params.current_contacts);
  }
  
  // Description: Create a new approval group
  async function createNewApprovalGroup(params: { groupName: string; approvers: UserProfile[]; requiredApprovals: number; }) {
    const mapped_approvers = params.approvers.map((approver: UserProfile) => {
      return {
        user_id: approver.address,
        account_version: approver.account_version || 0,
        required: false,
      };
    });
    const api_params = {
      userId: current_account.user_id,
      orgId: org_info.org_id,
      name: params.groupName,
      optionalsRequired: params.requiredApprovals,
      approvers: mapped_approvers,
    };
    createApprovalGroup(api_params)
      .unwrap()
      .then(() => {
        handleAction({ actionType: AdminUIActionTypes.Refresh });
        dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_creating_approval_group)));
      })
      .catch((msg) => {
        const message = msg.status === 409 ? AdminErrorMessages.existing_approval_group : AdminErrorMessages.error_creating_approval_group;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr), { msg }));
      });
  }

  // Description: Get the maximum number of optional approvals
  function maxOptionals(): number[] {
    const numbers: number[] = editing_users.map((_, index) => index + 2);
    if (numbers.length > 1) {
      numbers.splice(numbers.length - 2);
    }
    return numbers;
  }

  // Description: Reset the form, clear the group name and reset the touched state of the group_name field
  function reset() {
    !!user_list_ref.current && user_list_ref.current.handlePageActions({ actionType: UserListActionTypes.Reset});
    setGroupName("");
    formik.setFieldValue("group_name", "");
    formik.setFieldTouched("group_name", false);
  }

  // Description: react fragment for Remove button for each user
  function removeButton(user: UserProfile) {
    return <Button
      type="submit"
      variant="icon"
      aria-label={`Remove ${user.name} from approvers`}
      className="text-muted-light float-end"
      onClick={() => !!user_list_ref.current && user_list_ref.current.handlePageActions({ actionType: UserListActionTypes.RemoveProfile, params: user })}>
        <Icon className="ficon-x" />
    </Button>;
  }

  function handleSubmit() {
    if (!!formik.errors.group_name || !formik.values.group_name) {
      return;
    }
    const current_group_name = formik.values.group_name.trim();
    createNewApprovalGroup({ groupName: current_group_name, approvers: editing_users, requiredApprovals: required_approvals });
    handleAction({ actionType: AdminUIActionTypes.SetShow });
  }

  return (
    <>
      <Form.Group>
        <Form.Label htmlFor="group_name_tb">Name this Group</Form.Label>
        <Form.Control
          id="group_name_tb"
          ref={group_name_ref}
          type="text"
          name="group-name"
          placeholder="Enter Required Group Name"
          value={formik.values.group_name}
          onChange={formik.handleChange("group_name")}
          onBlur={formik.handleBlur("group_name")}
          autoFocus={true}
          isInvalid={!!formik.errors.group_name}
        />
        {!!formik.errors.group_name ? (
          <small className="error">{formik.errors.group_name}</small>
        ) : null}
      </Form.Group>

      <div className="mt-3">
        <Toast bg="warning" show={true} className="mt-0">
          <Toast.Body className="d-flex justify-content-between">
            Warning: Approval groups cannot be changed after they are created. <a target="_blank" href={SupportRoutes.approval_groups} rel="noreferrer">
              <Icon className="pv-icon-question text-muted" />
            </a>
          </Toast.Body>
        </Toast>
      </div>
      <h4 className="mt-3">Add Approvers</h4>
      <p>
        Please add <u>three</u> or more people to your list of approvers.
      </p>
      {
        (!!current_account && !!org_full_users) &&
        <UserList
          placeholder="Start typing to add a user"
          ref={user_list_ref}
          current_account={current_account}
          handleAction={handleUpdateUsers}
          hide_badges={true}
          org_users={org_full_users}
          user_ids={editing_users.map((e) => e.address)} />
      }
      {editing_users.length > 0 && (
        <div className="table-display mt-3">
          <Row className="header-card-row">
            <Col sm={5}>User</Col>
            <Col sm={6}>Role</Col>
          </Row>
          <div className="table-body">
            {_.map(editing_users, (user_profile: UserProfile, _i: number) => {
              const key = _.uniqueId(user_profile.address);
              return (
                <Row key={key}>
                  <Col xs={12} sm={5} className="d-block d-sm-none">
                    {user_profile.name}
                    {removeButton(user_profile)}
                  </Col>
                  <Col xs={12} sm={5} className="d-none d-sm-block">
                    {user_profile.name}
                  </Col>
                  <Col sm={4} className="with_cap">
                    {user_profile.role}
                  </Col>
                  <Col className="d-none d-sm-block">{removeButton(user_profile)}</Col>
                </Row>
              );
            })}
          </div>
        </div>
      )}
      {editing_users.length > 2 && (
        <div className="align-center">
          How many approvals are required for recovery?
          { (maxOptionals().length > 1) ? <DropdownButton
            variant="outline-secondary"
            size="sm"
            className="ms-4 pv-dropdown"
            title={required_approvals}
          >
            {_.map(maxOptionals(), (number: number, i: number) => (
              <Dropdown.Item key={i} onClick={() => setRequiredApprovals(number)}>
                {number}
              </Dropdown.Item>
            ))}
          </DropdownButton> : <b className="ms-3">{required_approvals}</b>}
        </div>
      )}
      <div className="btn-panel mt-3">
        <Button disabled={editing_users.length < 3 || !!formik.errors.group_name} onClick={handleSubmit}>Create</Button>
        <Button variant="no-outline-primary" disabled={editing_users.length < 1 &&!formik.values.group_name} onClick={reset}>Reset</Button>
        <Button variant="no-outline-primary" onClick={() => handleAction({ actionType: AdminUIActionTypes.SetShow })}>Close</Button>
      </div>
    </>
  );
}

export default React.memo(CreateFormComponent);

